<template>
    <AdminLayout :crumbs="crumbs" :season_id="season_id">
        <template v-if="season">
            <EventHeader :season="season">
                <template #actions>
                    <button v-if="is_admin" type="button" class="btn btn-primary" @click="open_form(null)"><i class="bi bi-plus-lg"></i>Nuevo ranking</button>
                </template>
            </EventHeader>

            <p class="a-muted mb-3">Un ranking es una regla para ordenar a los equipos. Se muestra cuando una parada de una etapa lo tiene configurado.</p>

            <div class="a-card">
                <ul class="a-list" v-if="rankings.length">
                    <li v-for="rk in rankings" :key="rk.id" class="a-row">
                        <div class="rk-icon"><i class="bi" :class="rk.criterion == utils.RANKING_CRITERIA.TIME ? 'bi-stopwatch' : 'bi-trophy'"></i></div>
                        <div class="a-row-main">
                            <div class="a-row-title">{{ rk.name }}</div>
                            <div class="a-row-meta">
                                <span>{{ criterion_text(rk) }}</span>
                                <span>{{ scope_text(rk) }}</span>
                                <span v-if="usage(rk).length"><i class="bi bi-signpost-split"></i>En {{ usage(rk).map((u) => u.stop).join(', ') }}</span>
                                <span v-else class="text-warning-emphasis"><i class="bi bi-exclamation-circle"></i>No lo usa ninguna parada</span>
                            </div>
                        </div>
                        <div class="a-row-actions" v-if="is_admin">
                            <button type="button" class="btn btn-ghost btn-sm" @click="open_form(rk)"><i class="bi bi-pencil"></i>Editar</button>
                            <button type="button" class="btn btn-ghost btn-icon" title="Eliminar" @click="ask_delete(rk)"><i class="bi bi-trash3"></i></button>
                        </div>
                    </li>
                </ul>
                <div v-else class="a-empty">
                    <i class="bi bi-trophy"></i>
                    <div class="a-empty-title">Sin rankings</div>
                    <div>Crea uno (por ejemplo "Clasificación de etapa" por puntos) y añádelo a una parada para mostrarlo en pantalla.</div>
                </div>
            </div>
        </template>

        <!-- New / edit -->
        <div class="modal fade a-modal" ref="formModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="draft">
                    <div class="modal-header">
                        <h1 class="modal-title">{{ draft.id ? 'Editar ranking' : 'Nuevo ranking' }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-4">
                            <label class="form-label" for="rk-name">Nombre</label>
                            <input id="rk-name" type="text" class="form-control" v-model="draft.name" placeholder="Ej. Clasificación general">
                        </div>
                        <label class="form-label">Cómo se ordena</label>
                        <div class="choice-cards mb-4">
                            <label class="choice-card" :class="{ 'is-selected': draft.criterion == utils.RANKING_CRITERIA.POINTS }">
                                <input type="radio" class="visually-hidden" :value="utils.RANKING_CRITERIA.POINTS" v-model="draft.criterion">
                                <b><i class="bi bi-trophy me-1"></i>Por puntos</b>
                                <span>Aciertos × {{ season.base_score }} pts (los puntos por acierto del {{ season_label.toLowerCase() }}).</span>
                            </label>
                            <label class="choice-card" :class="{ 'is-selected': draft.criterion == utils.RANKING_CRITERIA.TIME }">
                                <input type="radio" class="visually-hidden" :value="utils.RANKING_CRITERIA.TIME" v-model="draft.criterion">
                                <b><i class="bi bi-stopwatch me-1"></i>Por rapidez</b>
                                <span>Tiempo medio de las respuestas correctas; gana el más rápido.</span>
                            </label>
                        </div>
                        <label class="form-label">Qué actividades cuentan</label>
                        <div class="choice-cards">
                            <label class="choice-card" :class="{ 'is-selected': draft.scope == utils.RANKING_SCOPES.STAGE }">
                                <input type="radio" class="visually-hidden" :value="utils.RANKING_SCOPES.STAGE" v-model="draft.scope">
                                <b>Solo la etapa</b>
                                <span>Las de la etapa en la que está la parada.</span>
                            </label>
                            <label class="choice-card" :class="{ 'is-selected': draft.scope == utils.RANKING_SCOPES.GLOBAL }">
                                <input type="radio" class="visually-hidden" :value="utils.RANKING_SCOPES.GLOBAL" v-model="draft.scope">
                                <b>Acumulado</b>
                                <span>Todo lo jugado hasta ese momento en el {{ season_label.toLowerCase() }}.</span>
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="save">{{ draft.id ? 'Guardar' : 'Crear ranking' }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete -->
        <div class="modal fade a-modal" ref="deleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="to_delete">
                    <div class="modal-header">
                        <h1 class="modal-title">Eliminar {{ to_delete.name }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <template v-if="usage(to_delete).length">Lo usan {{ usage(to_delete).length }} paradas ({{ usage(to_delete).map((u) => u.stop).join(', ') }}): dejarán de mostrarlo.</template>
                        <template v-else>No lo usa ninguna parada.</template>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="remove">Eliminar</button>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script>
import { Modal } from 'bootstrap'
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import EventHeader from '@/components/admin/EventHeader.vue'

// An event's rankings: name + how it orders (criterion) + what counts
// (scope) - the whole ranking model (server.js register_ranking). Shown on
// screen only through a stop's resources, so each one says where it's used.
export default {
    name: 'EventRankings',
    components: { AdminLayout, EventHeader },
    data(){
        return { utils, draft: null, to_delete: null };
    },
    computed: {
        season_id(){ return parseInt(this.$route.params.season_id, 10); },
        season(){ return (this.$store.state.game.seasons || []).find((s) => s.id == this.season_id) || null; },
        season_label(){ return utils.get_season_label(window.MODE); },
        crumbs(){
            return [
                { label: utils.get_season_label(window.MODE, true), to: { name: 'Panel' } },
                { label: this.season ? this.season.name : '…', to: { name: 'Stages', params: { season_id: this.season_id } } },
                { label: 'Rankings' },
            ];
        },
        is_admin(){
            const user = this.$store.state.game.user;
            return !!user && user.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        rankings(){ return (this.$store.state.game.rankings || []).filter((r) => r.season_id == this.season_id); },
    },
    mounted(){
        this.form_modal = new Modal(this.$refs.formModal);
        this.delete_modal = new Modal(this.$refs.deleteModal);
    },
    beforeUnmount(){
        this.form_modal.dispose();
        this.delete_modal.dispose();
    },
    methods: {
        criterion_text(rk){
            return rk.criterion == utils.RANKING_CRITERIA.TIME ? 'Por rapidez' : 'Por puntos';
        },
        scope_text(rk){
            return rk.scope == utils.RANKING_SCOPES.STAGE ? 'Solo la etapa' : 'Acumulado';
        },
        // Stops (sector resources) of this event's stages that show it.
        usage(rk){
            const game = this.$store.state.game;
            const stages = (game.stages || []).filter((s) => s.season_id == this.season_id);
            const out = [];
            (game.sectors || []).forEach((se) => {
                const stage = stages.find((s) => s.id == se.stage_id);
                if(stage && (se.resources || []).some((r) => r.type == 'ranking' && r.ranking_id == rk.id))
                    out.push({ stop: se.name, stage: stage.name });
            });
            return out;
        },
        open_form(rk){
            this.draft = rk
                ? { id: rk.id, season_id: rk.season_id, name: rk.name, criterion: rk.criterion || utils.RANKING_CRITERIA.POINTS, scope: rk.scope || utils.RANKING_SCOPES.GLOBAL }
                : { season_id: this.season_id, name: '', criterion: utils.RANKING_CRITERIA.POINTS, scope: utils.RANKING_SCOPES.STAGE };
            this.form_modal.show();
        },
        save(){
            if(!utils.is_valid_parameter(this.draft.name)){
                window.toast.error('Ponle un nombre al ranking.');
                return;
            }
            window.network.wso.send(JSON.stringify({ action: this.draft.id ? window.RPC.EDIT_RANKING : window.RPC.REGISTER_RANKING, params: this.draft }));
            this.form_modal.hide();
        },
        ask_delete(rk){
            this.to_delete = rk;
            this.delete_modal.show();
        },
        remove(){
            window.network.wso.send(JSON.stringify({ action: window.RPC.REMOVE_RANKING, params: this.to_delete.id }));
            this.delete_modal.hide();
        }
    }
}
</script>

<style lang="scss" scoped>
.rk-icon{
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--a-accent-soft);
    color: var(--a-accent-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    flex-shrink: 0;
}
.choice-cards{ display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.choice-card{
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px;
    border: 1px solid var(--a-line-strong);
    border-radius: 8px;
    cursor: pointer;
    background: var(--a-surface);
    span{ font-size: 0.8rem; color: var(--a-text-2); }
}
.choice-card.is-selected{ border-color: var(--a-accent); box-shadow: 0 0 0 2px var(--a-accent-soft); }
</style>
